import { apiError } from "../../../../../utils/formErrors";
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Stack, TextField, MenuItem, CircularProgress, Dialog, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TopBar from "../../../../../component/TopBar";
import MainBox from "../../../container/MainBox";
import BtnPrimary from "../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../component/SecondaryButton";
import ReopenDialog from "../../../../../component/performance/ReopenDialog";
import SectionLoader from "../../../../../component/SectionLoader";
import EmptyState from "../../../../../component/EmptyState";
import { StatusChip } from "../../../../../component/listing";
// The status label and tone are declared once, beside the review columns.
import { REVIEW_LABEL, REVIEW_TONE } from "./reviewColumns";
import { getReview, submitSelfReview, submitManagerReview, finalizeReview, reopenReview } from "../../../../../services/performance";
import { promoteEmployee } from "../../../../../services/employee";
import { getGradeLevels } from "../../../../../services/gradeLevel";
import { getDesignations } from "../../../../../services/designation";
import { useNotify } from "../../../../../component/NotificationProvider";
import { tokens, displayFont } from "../../../../../theme";

const nameOf = (e) => (e ? `${e.fname || ""} ${e.lname || ""}`.trim() : "—");

// editable assessment block: per-competency score + comment, plus a summary
const Assessment = ({ title, competencies, scaleMax, value, onChange, onSubmit, busy, locked, submitLabel }) => {
  const setRating = (comp, field, v) => {
    const ratings = [...(value.ratings || [])];
    const idx = ratings.findIndex((r) => r.competency === comp);
    if (idx >= 0) ratings[idx] = { ...ratings[idx], [field]: v };
    else ratings.push({ competency: comp, [field]: v });
    onChange({ ...value, ratings });
  };
  const get = (comp, field) => (value.ratings || []).find((r) => r.competency === comp)?.[field] ?? "";
  return (
    <Box sx={{ flex: 1, minWidth: "20rem", p: "1.25rem", borderRadius: "12px", border: `1px solid ${tokens.border}`, background: tokens.surface }}>
      <Box sx={{ fontSize: "1rem", fontWeight: 600, color: tokens.text, mb: "1rem" }}>{title}</Box>
      <Stack spacing={1.5}>
        {(competencies || []).map((c) => (
          <Box key={c.name}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "0.5rem" }}>
              <Box sx={{ fontSize: "0.875rem", fontWeight: 600, color: tokens.text }}>{c.name} <Box component="span" sx={{ color: tokens.textMuted, fontWeight: 400 }}>×{c.weight}</Box></Box>
              <TextField select size="small" disabled={locked} value={get(c.name, "score")} onChange={(e) => setRating(c.name, "score", Number(e.target.value))} sx={{ width: "7rem" }}>
                {Array.from({ length: scaleMax }, (_, i) => i + 1).map((n) => <MenuItem key={n} value={n}>{n} / {scaleMax}</MenuItem>)}
              </TextField>
            </Stack>
            <TextField fullWidth size="small" disabled={locked} placeholder="Comment (optional)" value={get(c.name, "comment")} onChange={(e) => setRating(c.name, "comment", e.target.value)} />
          </Box>
        ))}
        <Box>
          <Box sx={{ fontSize: "0.8125rem", fontWeight: 500, color: tokens.textMuted, mb: "0.5rem" }}>Summary</Box>
          <TextField fullWidth size="small" multiline minRows={2} disabled={locked} value={value.summary || ""} onChange={(e) => onChange({ ...value, summary: e.target.value })} />
        </Box>
        {!locked && (
          <Box sx={{ textAlign: "right" }}>
            <BtnPrimary onClick={onSubmit} disabled={busy} sx={{ borderRadius: "8px" }}>{busy ? <CircularProgress size={"1rem"} /> : submitLabel}</BtnPrimary>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

const ReviewDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [self, setSelf] = useState({ ratings: [], summary: "" });
  const [manager, setManager] = useState({ ratings: [], summary: "" });
  const [outcomeOpen, setOutcomeOpen] = useState(false);
  const [outcome, setOutcome] = useState({ changeType: "promotion", designation: "", grade: "", newCtc: "" });
  const [grades, setGrades] = useState([]);
  const [designations, setDesignations] = useState([]);

  useEffect(() => {
    getGradeLevels().then((r) => setGrades(Array.isArray(r?.data) ? r.data : [])).catch(() => {});
    getDesignations().then((r) => setDesignations(Array.isArray(r?.data) ? r.data : [])).catch(() => {});
  }, []);

  const pickDesignation = (name) => {
    const d = designations.find((x) => x.name === name);
    const g = d?.grade && grades.find((x) => String(x._id) === String(d.grade));
    setOutcome((o) => ({ ...o, designation: name, ...(g ? { grade: g.name } : {}) }));
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getReview(id);
      const r = res?.data;
      setReview(r);
      setSelf({ ratings: r?.self?.ratings || [], summary: r?.self?.summary || "" });
      setManager({ ratings: r?.manager?.ratings || [], summary: r?.manager?.summary || "" });
    } catch (e) { notify.error(e?.message || "Failed to load review"); }
    finally { setLoading(false); }
  }, [id, notify]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const run = async (fn, payload, msg) => {
    setBusy(true);
    try { const res = await fn(id, payload); if (res?.success === false) throw new Error(res?.message); notify.success(msg); fetchData(); }
    catch (e) { notify.error(e?.message || "Action failed"); } finally { setBusy(false); }
  };

  const openOutcome = () => {
    setOutcome({ changeType: "promotion", designation: review?.employee?.designation || "", grade: review?.employee?.grade || "", newCtc: "" });
    setOutcomeOpen(true);
  };

  const applyOutcome = async () => {
    setBusy(true);
    try {
      const payload = {
        changeType: outcome.changeType,
        designation: outcome.designation || undefined,
        grade: outcome.grade || undefined,
        reason: `${review?.cycle?.name || "Appraisal"} outcome (rating ${review?.overallRating})`,
      };
      if (outcome.newCtc !== "" && outcome.newCtc != null) payload.newCtc = Number(outcome.newCtc);
      const res = await promoteEmployee(review.employee._id, payload);
      if (res?.success === false) throw apiError(res);
      notify.success("Outcome applied — career timeline updated");
      if (res?.data?.bandWarning) notify.warning?.(res.data.bandWarning) || notify.error(res.data.bandWarning);
      setOutcomeOpen(false);
    } catch (e) { notify.error(e?.message || "Could not apply outcome"); }
    finally { setBusy(false); }
  };

  if (loading) return <Box sx={{ width: "100%" }}><TopBar MainHeading={"Review"} /><MainBox sx={{ padding: "1.25rem" }}><SectionLoader /></MainBox></Box>;
  if (!review) return <Box sx={{ width: "100%" }}><TopBar MainHeading={"Review"} /><EmptyState title="Review not found" /></Box>;

  const cycle = review.cycle || {};
  const scaleMax = cycle.ratingScale?.max || 5;
  const finalized = review.status === "finalized";

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={"Review"} breadcrumbs={[{ label: "Performance", href: "/app/performance" }, { label: cycle.name || "Review" }]} />
      <MainBox sx={{ padding: "1.25rem" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.5rem", flexWrap: "wrap", gap: "1rem" }}>
          <Box>
            <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>{nameOf(review.employee)}</Box>
            <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{review.employee?.designation || ""} · {cycle.name} · Reviewer {nameOf(review.reviewer)}</Box>
          </Box>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {review.overallRating != null && <Box sx={{ fontSize: "0.875rem", fontWeight: 600, color: tokens.text }}>Overall {review.overallRating} / {scaleMax}</Box>}
            <StatusChip label={REVIEW_LABEL[review.status]} tone={REVIEW_TONE[review.status]} />
          </Stack>
        </Stack>

        <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
          <Assessment title="Self-assessment" competencies={cycle.competencies} scaleMax={scaleMax} value={self} onChange={setSelf}
            onSubmit={() => run(submitSelfReview, self, "Self-assessment saved")} busy={busy} locked={finalized} submitLabel="Save self-assessment" />
          <Assessment title="Manager review" competencies={cycle.competencies} scaleMax={scaleMax} value={manager} onChange={setManager}
            onSubmit={() => run(submitManagerReview, manager, "Manager review saved")} busy={busy} locked={finalized} submitLabel="Save manager review" />
        </Stack>

        {review.status === "manager_completed" && (
          <Box sx={{ mt: "1.5rem", textAlign: "right" }}>
            <BtnPrimary onClick={() => run(finalizeReview, undefined, "Review finalized")} disabled={busy} sx={{ borderRadius: "8px" }}>Finalize review</BtnPrimary>
          </Box>
        )}
        {finalized && (
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: "1.5rem", flexWrap: "wrap", gap: "1rem" }}>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", px: "1rem", py: "0.5rem", borderRadius: "8px", background: tokens.successTint }}>
              <Box>
                <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.text }}>Finalized — rating {review.overallRating} / {scaleMax}</Box>
                {(review.reopenings || []).length > 0 && (
                  <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mt: "0.25rem" }}>
                    Reopened {review.reopenings.length} time{review.reopenings.length === 1 ? "" : "s"} · last: {review.reopenings[review.reopenings.length - 1].reason}
                  </Box>
                )}
              </Box>
            </Box>
            <Stack direction="row" spacing={1.5}>
              {/* the usual repair: a rating entered wrongly, or finalized before
                  the conversation happened. One review, not the whole cycle. */}
              <BtnSecondary onClick={() => setReopenOpen(true)} sx={{ borderRadius: "8px" }}>Reopen</BtnSecondary>
              <BtnPrimary onClick={openOutcome} sx={{ borderRadius: "8px" }}>Apply outcome</BtnPrimary>
            </Stack>
          </Stack>
        )}
      </MainBox>
      <ReopenDialog
        open={reopenOpen}
        title="Reopen this review?"
        consequence="It returns to manager-completed so the rating can be changed, then finalized again. The current rating is kept and what it was is recorded."
        confirmLabel="Reopen review"
        onClose={() => setReopenOpen(false)}
        onConfirm={async (reason) => {
          const res = await reopenReview(review._id, reason);
          if (res?.success !== false) { notify.success("Review reopened"); fetchData(); }
          return res;
        }}
      />

      <Dialog open={outcomeOpen} onClose={() => setOutcomeOpen(false)} PaperProps={{ sx: { borderRadius: "12px", width: "min(30rem,92vw)" } }}>
        <Box sx={{ p: "1.5rem" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb="1rem">
            <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>Apply review outcome</Box>
            <IconButton aria-label="Close" size="small" onClick={() => setOutcomeOpen(false)}><CloseIcon fontSize="small" /></IconButton>
          </Stack>
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "1.25rem" }}>
            Records a promotion/transfer on {nameOf(review.employee)}'s career timeline — and a salary revision if you set a new CTC.
          </Box>
          <Box sx={{ fontSize: "0.8125rem", fontWeight: 500, color: tokens.textMuted, mb: "0.5rem" }}>Type</Box>
          <TextField select fullWidth size="small" value={outcome.changeType} onChange={(e) => setOutcome((o) => ({ ...o, changeType: e.target.value }))} sx={{ mb: "1rem" }}>
            {[["promotion", "Promotion"], ["reassignment", "Role change"], ["transfer", "Transfer"]].map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
          </TextField>
          <Stack direction="row" spacing={1.25} sx={{ mb: "1rem" }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ fontSize: "0.8125rem", fontWeight: 500, color: tokens.textMuted, mb: "0.5rem" }}>Designation</Box>
              {designations.length > 0 ? (
                <TextField select fullWidth size="small" value={outcome.designation} onChange={(e) => pickDesignation(e.target.value)}>
                  <MenuItem value="">—</MenuItem>
                  {designations.map((d) => <MenuItem key={d._id} value={d.name}>{d.name}</MenuItem>)}
                </TextField>
              ) : (
                <TextField fullWidth size="small" value={outcome.designation} onChange={(e) => setOutcome((o) => ({ ...o, designation: e.target.value }))} />
              )}
            </Box>
            <Box sx={{ width: "7rem" }}>
              <Box sx={{ fontSize: "0.8125rem", fontWeight: 500, color: tokens.textMuted, mb: "0.5rem" }}>Grade</Box>
              {grades.length > 0 ? (
                <TextField select fullWidth size="small" value={outcome.grade} onChange={(e) => setOutcome((o) => ({ ...o, grade: e.target.value }))}>
                  <MenuItem value="">—</MenuItem>
                  {grades.map((g) => <MenuItem key={g._id} value={g.name}>{g.name}</MenuItem>)}
                </TextField>
              ) : (
                <TextField fullWidth size="small" value={outcome.grade} onChange={(e) => setOutcome((o) => ({ ...o, grade: e.target.value }))} />
              )}
            </Box>
          </Stack>
          <Box sx={{ fontSize: "0.8125rem", fontWeight: 500, color: tokens.textMuted, mb: "0.5rem" }}>New CTC (optional)</Box>
          <TextField fullWidth size="small" type="number" value={outcome.newCtc} placeholder="Leave blank to keep current pay" onChange={(e) => setOutcome((o) => ({ ...o, newCtc: e.target.value }))} sx={{ mb: "1.5rem" }} />
          <Stack direction="row" spacing={1.5} justifyContent="space-between">
            <BtnSecondary onClick={() => navigate(`/app/employee-details/${review.employee._id}/career`)} sx={{ borderRadius: "8px" }}>View timeline</BtnSecondary>
            <Stack direction="row" spacing={1.5}>
              <BtnSecondary onClick={() => setOutcomeOpen(false)} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
              <BtnPrimary onClick={applyOutcome} disabled={busy} sx={{ borderRadius: "8px" }}>{busy ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Apply"}</BtnPrimary>
            </Stack>
          </Stack>
        </Box>
      </Dialog>
    </Box>
  );
};

export default ReviewDetail;
